import ContactProfileImage from '@app/components/ContactProfileImage'
import {
  actionCreators,
  IGlobalSearchInstanceState,
} from '@app/components/GlobalSearch/modules/globalSearch'
import PropertyThumbnailImage from '@app/components/PropertyThumbnailImage'
import TypeAheadList from '@app/components/TypeAheadList'
import { EntityAllow, IEntityAllow } from '@app/models/EntityAllow'
import { EntityAccessType, EntityAccessValue } from '@app/models/enums'
import { bindFunctions } from '@app/utils/class'
import { formatAddressSingleLine } from '@app/utils/formatting'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { List } from 'immutable'
import * as React from 'react'
import { Input } from 'reactstrap'

interface IComponentProps {
  onChange: (allowedAccess: List<IEntityAllow>) => void
  value: List<IEntityAllow>
}

export default class AccessEditor extends React.PureComponent<
  IComponentProps & IGlobalSearchInstanceState & typeof actionCreators
> {
  public constructor(props: any) {
    super(props)

    bindFunctions(this)
  }

  public render() {
    if (!this.props.results) {
      return
    }

    const resultsArray = this.props.results.contacts
      .concat(this.props.results.properties)
      .toArray()

    return (
      <div>
        <TypeAheadList
          clearInputAfterSelection={true}
          dataSource={resultsArray}
          value=''
          placeholder='Search...'
          labelKey='id'
          _renderSuggestion={this._renderItem}
          _onSuggestionFetchRequested={this._getResults}
          onChange={this._addAccess}
        />

        {this._renderAccess()}
      </div>
    )
  }

  private _addAccess(item: any) {
    if (item.propertyType) {
      const entities = this.props.value.toArray()
      entities.push({
        authenticatorId: item.id,
        hasChanged: true,
        isAdded: true,
        name: item.name,
        type: EntityAccessType.Property,
        value: EntityAccessValue.ViewOnly,
      })
      this.props.onChange(List(entities))
    } else {
      const entities = this.props.value.toArray()
      entities.push({
        authenticatorId: item.id,
        hasChanged: true,
        isAdded: true,
        name: `${item.firstName} ${item.lastName}`,
        type: EntityAccessType.Contact,
        value: EntityAccessValue.ViewOnly,
      })

      this.props.onChange(List(entities))
    }
  }

  private _removeAccess(id: string) {
    this.props.onChange(
      this.props.value.remove(
        this.props.value.findIndex((item: any) => {
          return item.authenticatorId === id
        }),
      ),
    )
  }

  private _renderAccess() {
    const access = this.props.value.map((item: any, i: any) => {
      const classNames = `${
        i % 2 === 1 ? 'striped-row' : ''
      } d-flex flex-row align-items-center justify-content-between mt-2 py-2`
      return (
        <div key={item.authenticatorId} className={classNames}>
          <strong className='ml-2 overflow-text' style={{ width: '70%' }}>
            {item.name}
          </strong>
          <div className='d-flex flex-row align-items-center'>
            <Input
              type='select'
              value={item.value}
              onChange={(e) =>
                this._changeAllowedAccess(item.authenticatorId, e.target.value)
              }
            >
              <option value='ViewOnly'>View Only</option>
              <option value='OwnAttachments'>Own Attachments</option>
              <option value='Edit'>Edit</option>
              <option value='Unrestricted'>Unrestricted</option>
            </Input>
            <a
              className='mx-2 text-danger'
              onClick={() => this._removeAccess(item.authenticatorId)}
            >
              <FontAwesomeIcon icon={['fal', 'trash']} />
            </a>
          </div>
        </div>
      )
    })

    return <div className='mt-2 ml-2'>{access}</div>
  }

  private _changeAllowedAccess(id: string, value: string) {
    const entities = this.props.value.toArray()

    this.props.onChange(
      List(
        entities.map((item: IEntityAllow) => {
          let entity = EntityAllow.fromJS(item)
          if (entity.authenticatorId === id) {
            entity = entity.set('value', value).toJS()
          }

          return entity
        }),
      ),
    )
  }

  private _getResults(item: any) {
    this.props.searchUnrestricted('access-editor', item.value)
  }

  private _renderItem(suggestion: any) {
    if (suggestion.propertyType) {
      return (
        <div className='d-flex flex-row'>
          <div className='align-self-center'>
            <PropertyThumbnailImage property={suggestion} size={35} />
          </div>
          <div className='flex-grow-1 ml-2'>
            <div className='font-weight-bold'>{suggestion.name}</div>
            <div className='small'>
              {formatAddressSingleLine(suggestion.address)}
            </div>
          </div>
        </div>
      )
    } else {
      return (
        <div className='d-flex flex-row'>
          <div className='align-self-center'>
            <ContactProfileImage contact={suggestion} size={35} />
          </div>
          <div className='flex-grow-1 ml-2'>
            <div className='font-weight-bold'>
              {suggestion.firstName} {suggestion.lastName}
            </div>
            {suggestion.company && (
              <div className='small'>{suggestion.company.name}</div>
            )}
          </div>
        </div>
      )
    }
  }
}
